import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Navigate, useParams } from 'react-router-dom';
import RichTextEditor from '../../components/ui/RichTextEditor';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import CommunityPublicProfilePreview from '../../components/hubup/CommunityPublicProfilePreview';
import { useUserAccess } from '../../context/UserAccessContext';
import { useHubupCommunities } from '../../context/HubupCommunitiesContext';
import { canManageHubupCommunitySettings } from '../../utils/hubupAccess';
import {
  buildHubupPublicProfileUrl,
  deleteHubupGroupCommunityIcon,
  fetchHubupGroup,
  hubupGroupIconImgSrc,
  patchHubupGroupPublicProfile,
  resolveHubupAssetUrl,
  uploadHubupGroupCommunityIcon,
  uploadHubupNotificationImage,
} from '../../api/hubup';

const inputClass =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';

function profileHtmlInitial(raw) {
  const h = String(raw ?? '').trim();
  return h || '<p></p>';
}

const PROFILE_INLINE_IMAGE_MAX_BYTES = 3 * 1024 * 1024;
const PROFILE_INLINE_VIDEO_MAX_BYTES = 50 * 1024 * 1024;

const PROFILE_IMAGE_MIMES = new Set([
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
]);

const PROFILE_VIDEO_MIMES = new Set([
  'video/mp4',
  'video/webm',
  'video/quicktime',
]);

function isProfileInlineImageFile(file) {
  const t = String(file?.type || '')
    .trim()
    .toLowerCase();
  if (t && PROFILE_IMAGE_MIMES.has(t)) return true;
  return /\.(png|jpe?g|gif|webp)$/i.test(String(file?.name || ''));
}

function isProfileInlineVideoFile(file) {
  const t = String(file?.type || '')
    .trim()
    .toLowerCase();
  if (t && PROFILE_VIDEO_MIMES.has(t)) return true;
  return /\.(mp4|webm|mov)$/i.test(String(file?.name || ''));
}

export default function HubupPublicProfile() {
  const { groupId } = useParams();
  const { user } = useUserAccess();
  const { communities, refetch: refetchCommunities } = useHubupCommunities();
  const canManageHubup = canManageHubupCommunitySettings(user);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedHint, setSavedHint] = useState('');
  const savedHintTimerRef = useRef(null);

  const [communityName, setCommunityName] = useState('');
  const [profileSlug, setProfileSlug] = useState('');
  const [profileTitle, setProfileTitle] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [profileAdditionalInfoHtml, setProfileAdditionalInfoHtml] =
    useState('<p></p>');
  const [groupIconUrl, setGroupIconUrl] = useState('');
  const [iconCacheBust, setIconCacheBust] = useState(null);
  const [iconBusy, setIconBusy] = useState(false);
  const [iconError, setIconError] = useState(null);
  const iconInputRef = useRef(null);

  const currentCommunity = useMemo(
    () => communities.find((c) => c.id === groupId),
    [communities, groupId],
  );

  const publicProfileUrl = useMemo(
    () => buildHubupPublicProfileUrl(profileSlug),
    [profileSlug],
  );

  useEffect(() => {
    setGroupIconUrl(String(currentCommunity?.groupIconUrl ?? '').trim());
  }, [currentCommunity?.groupIconUrl, groupId]);

  const load = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetchHubupGroup(groupId);
      const g = res?.data?.group;
      if (!g) {
        setLoadError('Détail communauté indisponible.');
        return;
      }
      setCommunityName(String(g.name ?? currentCommunity?.name ?? '').trim());
      setProfileSlug(String(g.profileSlug ?? '').trim());
      setProfileTitle(String(g.profileTitle ?? '').trim());
      setProfileDescription(String(g.profileDescription ?? '').trim());
      setProfileAdditionalInfoHtml(
        profileHtmlInitial(g.profileAdditionalInfoHtml),
      );
    } catch {
      setLoadError('Impossible de charger le profil public.');
    } finally {
      setLoading(false);
    }
  }, [groupId, currentCommunity?.name]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!savedHint) return undefined;
    if (savedHintTimerRef.current) clearTimeout(savedHintTimerRef.current);
    savedHintTimerRef.current = setTimeout(() => {
      setSavedHint('');
      savedHintTimerRef.current = null;
    }, 2500);
    return () => {
      if (savedHintTimerRef.current) clearTimeout(savedHintTimerRef.current);
    };
  }, [savedHint]);

  const copyPublicUrl = async () => {
    if (!publicProfileUrl) return;
    try {
      await navigator.clipboard.writeText(publicProfileUrl);
      setSavedHint('Lien copié.');
    } catch {
      setSavedHint('Copie impossible (navigateur).');
    }
  };

  const uploadProfileInlineMedia = useCallback(async (file, kind) => {
    const isVideo = kind === 'video';
    if (isVideo) {
      if (!isProfileInlineVideoFile(file)) {
        throw new Error('Vidéos MP4, WebM ou MOV uniquement.');
      }
      if (file.size > PROFILE_INLINE_VIDEO_MAX_BYTES) {
        throw new Error(`Vidéo trop volumineuse (max 50 Mo) : ${file.name}`);
      }
    } else if (!isProfileInlineImageFile(file)) {
      throw new Error('Images PNG, JPEG, WebP ou GIF uniquement.');
    } else if (file.size > PROFILE_INLINE_IMAGE_MAX_BYTES) {
      throw new Error(`Image trop volumineuse (max 3 Mo) : ${file.name}`);
    }

    const res = await uploadHubupNotificationImage(file);
    const path = String(res?.data?.path ?? '').trim();
    if (!path) throw new Error('Envoi du fichier impossible.');
    return resolveHubupAssetUrl(path);
  }, []);

  const handleProfileInlineImageUpload = useCallback(
    async (file) => {
      try {
        return await uploadProfileInlineMedia(file, 'image');
      } catch (err) {
        setSaveError(
          err?.message || err?.data?.message || 'Upload image impossible.',
        );
        throw err;
      }
    },
    [uploadProfileInlineMedia],
  );

  const handleProfileInlineVideoUpload = useCallback(
    async (file) => {
      try {
        return await uploadProfileInlineMedia(file, 'video');
      } catch (err) {
        setSaveError(
          err?.message || err?.data?.message || 'Upload vidéo impossible.',
        );
        throw err;
      }
    },
    [uploadProfileInlineMedia],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!groupId || !canManageHubup) return;
    setSaving(true);
    setSaveError(null);
    setSavedHint('');
    try {
      const res = await patchHubupGroupPublicProfile(groupId, {
        profileTitle: profileTitle.trim(),
        profileDescription: profileDescription.trim(),
        profileAdditionalInfoHtml: profileAdditionalInfoHtml || '',
      });
      const g = res?.data?.group;
      if (g) {
        setProfileSlug(String(g.profileSlug ?? '').trim());
        setProfileTitle(String(g.profileTitle ?? '').trim());
        setProfileDescription(String(g.profileDescription ?? '').trim());
        setProfileAdditionalInfoHtml(
          profileHtmlInitial(g.profileAdditionalInfoHtml),
        );
      }
      setSavedHint('Profil public enregistré.');
    } catch (err) {
      setSaveError(
        err?.message ||
          'Enregistrement impossible. Vérifiez les champs et réessayez.',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleIconFile = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !groupId || !canManageHubup) return;
    const maxBytes = 2 * 1024 * 1024;
    if (file.size > maxBytes) {
      setIconError('Image trop volumineuse (max 2 Mo).');
      return;
    }
    setIconError(null);
    setIconBusy(true);
    try {
      const res = await uploadHubupGroupCommunityIcon(groupId, file);
      const url = res?.data?.community?.groupIconUrl;
      if (url) setGroupIconUrl(String(url));
      setIconCacheBust(Date.now());
      await refetchCommunities();
      setSavedHint('Icône mise à jour.');
    } catch {
      setIconError('Envoi de l’icône impossible (format ou taille refusé).');
    } finally {
      setIconBusy(false);
    }
  };

  const handleDeleteIcon = async () => {
    if (!groupId || !canManageHubup || !groupIconUrl) return;
    if (!window.confirm('Retirer l’icône de cette communauté ?')) return;
    setIconError(null);
    setIconBusy(true);
    try {
      await deleteHubupGroupCommunityIcon(groupId);
      setGroupIconUrl('');
      setIconCacheBust(null);
      await refetchCommunities();
      setSavedHint('Icône retirée.');
    } catch {
      setIconError('Suppression de l’icône impossible.');
    } finally {
      setIconBusy(false);
    }
  };

  const iconPreviewSrc = hubupGroupIconImgSrc(groupIconUrl, iconCacheBust);

  if (!canManageHubup && groupId) {
    return <Navigate to={`/hubup/g/${groupId}/dashboard`} replace />;
  }

  return (
    <>
      <Helmet>
        <title>Profil publique — HubUP</title>
      </Helmet>
      <main className="main-content">
        <div className="mb-6 md:mb-8">
          <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
            Profil publique
          </h1>
          <p className="text-sm text-muted-foreground">
            Modifier votre page de profil publique.
          </p>
        </div>

        {loadError ? (
          <p className="text-xs text-error mb-4">{loadError}</p>
        ) : null}

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
          <form
            onSubmit={handleSubmit}
            className="bg-card rounded-lg shadow-elevation-2 border border-border p-4 md:p-6 space-y-5"
          >
            <div>
              <label className="text-xs font-medium text-foreground mb-2 block">
                Icône de la communauté
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <input
                  ref={iconInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  className="sr-only"
                  disabled={loading || iconBusy || !canManageHubup}
                  onChange={handleIconFile}
                />
                <div className="relative inline-block shrink-0">
                  <div
                    className={`flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-white shadow-elevation-1 ring-1 ring-[#E8E4FF] ${
                      iconBusy ? 'opacity-60' : ''
                    }`}
                  >
                    {iconBusy ? (
                      <Icon
                        name="Loader2"
                        size={28}
                        className="text-muted-foreground animate-spin"
                      />
                    ) : iconPreviewSrc ? (
                      <img
                        src={iconPreviewSrc}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <Icon
                        name="ImagePlus"
                        size={28}
                        className="text-muted-foreground"
                      />
                    )}
                  </div>
                  {groupIconUrl && canManageHubup && !iconBusy ? (
                    <button
                      type="button"
                      title="Retirer l’icône"
                      className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full border border-border bg-card text-muted-foreground shadow-sm hover:bg-destructive/15 hover:text-destructive"
                      onClick={() => void handleDeleteIcon()}
                    >
                      <Icon name="X" size={14} strokeWidth={2.5} />
                    </button>
                  ) : null}
                </div>
                <div className="min-w-0 space-y-2">
                  {canManageHubup ? (
                    <Button
                      type="button"
                      variant="outline"
                      iconName="Upload"
                      iconPosition="left"
                      disabled={loading || iconBusy}
                      onClick={() => iconInputRef.current?.click()}
                    >
                      {groupIconUrl ? 'Changer l’icône' : 'Ajouter une icône'}
                    </Button>
                  ) : null}
                  <p className="text-[11px] text-muted-foreground leading-relaxed">
                    PNG, JPEG, GIF ou WebP — max 2 Mo. Affichée sur la page
                    publique et dans le fil de notifications.
                  </p>
                  {iconError ? (
                    <p className="text-xs text-error">{iconError}</p>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-5">
              <label className="text-xs font-medium text-foreground mb-1 block">
                URL publique
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  readOnly
                  className={`${inputClass} font-mono text-xs bg-muted/40`}
                  value={profileSlug || '—'}
                  aria-readonly="true"
                />
                {publicProfileUrl ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="shrink-0"
                    iconName="ExternalLink"
                    iconPosition="left"
                    onClick={() => window.open(publicProfileUrl, '_blank', 'noopener,noreferrer')}
                  >
                    Ouvrir
                  </Button>
                ) : null}
              </div>
              {!profileSlug && !loading ? (
                <p className="text-[11px] text-muted-foreground mt-1.5">
                  Slug non disponible — enregistrez ou renommez la communauté côté
                  HubUP pour le générer.
                </p>
              ) : null}
              {publicProfileUrl ? (
                <button
                  type="button"
                  className="text-[11px] text-primary hover:underline mt-1.5 break-all text-left"
                  onClick={copyPublicUrl}
                >
                  {publicProfileUrl}
                </button>
              ) : null}
            </div>

            <div>
              <label className="text-xs font-medium text-foreground mb-1 block">
                Titre affiché
              </label>
              <input
                type="text"
                className={inputClass}
                value={profileTitle}
                disabled={loading || !canManageHubup}
                placeholder={communityName || 'Ex. Bienvenue au club'}
                onChange={(e) => setProfileTitle(e.target.value)}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Laissé vide : le nom de la communauté est utilisé.
              </p>
            </div>

            <div>
              <label className="text-xs font-medium text-foreground mb-1 block">
                Description courte
              </label>
              <textarea
                className={`${inputClass} min-h-[88px] resize-y`}
                value={profileDescription}
                disabled={loading || !canManageHubup}
                placeholder="Texte sous le titre sur la page publique"
                onChange={(e) => setProfileDescription(e.target.value)}
              />
            </div>

            <div>
              <label className="text-xs font-medium text-foreground mb-1 block">
                Informations complémentaires
              </label>
              <RichTextEditor
                value={profileAdditionalInfoHtml}
                onChange={setProfileAdditionalInfoHtml}
                readOnly={loading || !canManageHubup}
                minEditorHeight={180}
                enableImages
                enableVideos
                onImageUpload={handleProfileInlineImageUpload}
                onVideoUpload={handleProfileInlineVideoUpload}
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Images (max 3 Mo) et vidéos (max 50 Mo) via les boutons image et
                vidéo de la barre d’outils.
              </p>
            </div>

            {saveError ? (
              <p className="text-xs text-error">{saveError}</p>
            ) : null}
            {savedHint ? (
              <p className="text-xs text-emerald-700 dark:text-emerald-400">
                {savedHint}
              </p>
            ) : null}

            {canManageHubup ? (
              <div className="flex justify-end pt-1">
                <Button
                  type="submit"
                  variant="success"
                  iconName="Save"
                  iconPosition="left"
                  disabled={loading || saving}
                >
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Lecture seule — vous n&apos;avez pas les droits de modification.
              </p>
            )}
          </form>

          <aside className="lg:sticky lg:top-24 self-start">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3 px-1">
              Aperçu
            </p>
            {loading ? (
              <p className="text-sm text-muted-foreground px-1">Chargement…</p>
            ) : (
              <CommunityPublicProfilePreview
                profileTitle={profileTitle}
                profileDescription={profileDescription}
                profileAdditionalInfoHtml={profileAdditionalInfoHtml}
                groupIconUrl={groupIconUrl}
              />
            )}
          </aside>
        </div>
      </main>
    </>
  );
}
